// frontend/src/pages/Profile.js
// Build: 2026-05-08-profile-author-photo-upload-v1
// v14 goals:
// - FIX the breaking issue: Profile Preview must use App.js preview flag (sessionStorage), not a mismatched key.
// - Welcome greeting should show at every login until disabled (App.js already does per-session).
// - Preview should force greeting to show immediately on Home (even without a new login).
// - Keep App.js per-account disable keys strategy exactly (WELCOME_NS + per-ident disabled keys + legacy seen key).
// - Keep avatar fallback fix + live username parsing.
// - No backend changes.

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { api } from "../api";
import { useUser } from "../UserContext";
import StoryCoverCard from "../components/StoryCoverCard";
import NewsletterPreferencesV1 from "../components/NewsletterPreferencesV1";
import MentorPanel from "../components/MentorPanel";
import { getPublicationInfo } from "../utils/publicationStatus";

// Must match App.js namespace (per-account disable flag)
const WELCOME_NS = "ns.onboarding.welcome.v1";

// Legacy key used by older App.js logic (global)
const WELCOME_LEGACY_SEEN_KEY = "ns.onboarding.welcome.v1.seen";

// ✅ MUST match App.js Preview flag (sessionStorage) so AppShell reacts immediately
const WELCOME_PREVIEW_KEY = "ns.onboarding.welcome.v1.preview";

const FACEBOOK_APP_ID = String(process.env.REACT_APP_FACEBOOK_APP_ID || "").trim();

const PROFILE_TEXT = {
  en: {
    brand: "NEXTSCENES", title: "My Profile", restoring: "Restoring session…",
    subtitle: "Your public face should be calm and credible. Edit with intention, not impulse.",
    ownerView: "Owner view", memberSince: "Member since", working: "Working...", refresh: "Refresh",
    copyLink: "Copy profile link", copyTitle: "Copy the public profile link", copied: "Public profile link copied.", copyFailed: "Could not copy link.",
    previewPublic: "Preview public profile", previewTitle: "Preview how readers see your profile", goHome: "Go Home",
    profileSummary: "Profile summary", storylineInitiated: "Storyline initiated", storylinesInitiated: "Storylines initiated",
    loading: "Loading…", noProfile: "No profile data.", nsAuthor: "NextScenes Author", noBio: "No public bio yet. Click Edit profile to add one.",
    noLinks: "No public links.", storylinesInitiatedTile: "Storylines initiated", canonScenesWritten: "Canon scenes written",
    profileDetails: "Profile details", profileDetailsHelp: "Keep it clean. Readers respect clarity more than performance.", closeEdit: "Close edit", editProfile: "Edit profile", viewShelf: "View shelf", viewShelfTitle: "Jump to your story shelf",
    name: "Name", username: "Username", location: "Location", avatarUrl: "Avatar URL", authorPhoto: "Author photo", photoAdded: "Photo added", photoMissing: "No photo yet", publicLinks: "Public links", publicLinksReady: "Links ready", publicLinksMissing: "No links yet", advancedPhotoUrl: "Advanced photo URL", photoPreview: "Photo preview", displayName: "Display Name", publicName: "Your public name", bio: "Bio", bioPlaceholder: "What do you write about? What do you stand for?", website: "Website",
    saving: "Saving...", saveChanges: "Save changes", cancel: "Cancel", account: "Account", emailPrivate: "Email (private)", emailVerified: "Email verified", yes: "Yes", no: "No", status: "Status", active: "Active", disabled: "Disabled",
    preferences: "Preferences", prefOwner: "Owner", prefSub: "These settings affect this browser account. If you want calm, you control the first impression here.", welcomeGreeting: "Welcome greeting on login", preview: "Preview", previewGreetingTitle: "Force the greeting to show immediately on Home",
    enabled: "Enabled", prefEnabledHint: "Enabled means it can show on every login. Disabled blocks it for this account on this browser.", prefFootnote: "This is no longer a “first login only” setting. It is either enabled always, or disabled.", storefront: "Your profile is your storefront. Keep it honest and readable.",
    publicShelf: "Public Shelf", shelfHelp: "A serious writer is measured by pages, not speeches. These are the pages.", sceneMgmtNote: "Published books appear as finished works. Active storylines keep Scene Management for owners.", noPublicStorylines: "No public Storylines to show yet.", canon: "canon", lastUpdated: "Last updated", created: "Created", open: "Open", sceneManagement: "Scene Management", sceneManagementTitle: "Owner-only. Opens Writer Studio for this Storyline.", publishedOnAmazon: "Published on Amazon", publishedBook: "Published Book", viewPublishedBook: "View Published Book", viewOnAmazon: "View on Amazon", by: "By", activeStoryline: "Active storyline", ownerWorkshop: "Owner workspace",
    updateOk: "Profile updated.", updateFailed: "Failed to update profile.",
    welcomeEnabled: "Welcome greeting enabled (shows on every login).", welcomeDisabled: "Welcome greeting disabled.",
    uploadAuthorPhoto: "Upload author photo", photoUploadHint: "JPG, PNG, or WEBP. Maximum 5 MB.", photoUploading: "Uploading…", photoUploadSuccess: "Photo uploaded. Save changes to publish it on your profile.", photoUploadFailed: "Photo upload failed.", removePhoto: "Remove photo", photoHelp: "Photo help", photoHelpText: "Use a clear author photo or professional image. Keep it respectful and easy to recognize.", facebookSignIn: "Facebook sign-in", facebookLinked: "Facebook linked", facebookNotLinked: "Facebook not linked", linkFacebook: "Link Facebook", linkingFacebook: "Linking…", facebookLinkOk: "Facebook sign-in linked to this NextScenes account.", facebookLinkHelp: "Link Facebook once, then use it for future logins.", facebookHelpSummary: "Facebook login help", facebookHelpNewUsers: "New users can join directly with Facebook when Facebook provides an email.", facebookHelpExisting: "Existing users should log in first with their usual method, then link Facebook here. The Facebook email must match the NextScenes account email.", facebookHelpAfterLink: "After linking, Continue with Facebook will work for future logins.", facebookHelpWrongAccount: "If Facebook shows the wrong person, log out of Facebook in this browser or use a private window.", facebookLinkFailed: "Facebook linking failed.", facebookNotConfigured: "Facebook sign-in is not configured yet.",
  },
  fr: {
    brand: "NEXTSCENES", title: "Mon profil", restoring: "Restauration de la session…",
    subtitle: "Votre visage public doit être calme et crédible. Modifiez avec intention, pas par impulsion.",
    ownerView: "Vue propriétaire", memberSince: "Membre depuis", working: "Traitement...", refresh: "Actualiser",
    copyLink: "Copier le lien du profil", copyTitle: "Copier le lien public du profil", copied: "Lien du profil public copié.", copyFailed: "Impossible de copier le lien.",
    previewPublic: "Aperçu du profil public", previewTitle: "Voir comment les lecteurs voient votre profil", goHome: "Accueil",
    profileSummary: "Résumé du profil", storylineInitiated: "Storyline créée", storylinesInitiated: "Storylines créées",
    loading: "Chargement…", noProfile: "Aucune donnée de profil.", nsAuthor: "Auteur NextScenes", noBio: "Aucune biographie publique pour le moment. Cliquez sur Modifier le profil pour en ajouter une.",
    noLinks: "Aucun lien public.", storylinesInitiatedTile: "Storylines créées", canonScenesWritten: "Scènes Canon écrites",
    profileDetails: "Détails du profil", profileDetailsHelp: "Restez clair. Les lecteurs respectent la précision plus que la mise en scène.", closeEdit: "Fermer la modification", editProfile: "Modifier le profil", viewShelf: "Voir la bibliothèque", viewShelfTitle: "Aller à votre bibliothèque",
    name: "Nom", username: "Nom d’utilisateur", location: "Lieu", avatarUrl: "URL de l’avatar", authorPhoto: "Photo d’auteur", photoAdded: "Photo ajoutée", photoMissing: "Aucune photo", publicLinks: "Liens publics", publicLinksReady: "Liens prêts", publicLinksMissing: "Aucun lien", advancedPhotoUrl: "URL avancée de la photo", photoPreview: "Aperçu photo", displayName: "Nom public", publicName: "Votre nom public", bio: "Biographie", bioPlaceholder: "Sur quoi écrivez-vous ? Que défendez-vous ?", website: "Site web",
    saving: "Enregistrement...", saveChanges: "Enregistrer", cancel: "Annuler", account: "Compte", emailPrivate: "E-mail (privé)", emailVerified: "E-mail vérifié", yes: "Oui", no: "Non", status: "Statut", active: "Actif", disabled: "Désactivé",
    preferences: "Préférences", prefOwner: "Propriétaire", prefSub: "Ces paramètres affectent ce compte dans ce navigateur. Si vous voulez du calme, vous contrôlez ici la première impression.", welcomeGreeting: "Message d’accueil à la connexion", preview: "Aperçu", previewGreetingTitle: "Forcer l’affichage immédiat de l’accueil sur Home",
    enabled: "Activé", prefEnabledHint: "Activé signifie qu’il peut apparaître à chaque connexion. Désactivé le bloque pour ce compte dans ce navigateur.", prefFootnote: "Ce n’est plus un réglage “première connexion seulement”. Il est soit toujours activé, soit désactivé.", storefront: "Votre profil est votre vitrine. Gardez-le honnête et lisible.",
    publicShelf: "Bibliothèque publique", shelfHelp: "Un auteur sérieux se mesure aux pages, pas aux discours. Voici les pages.", sceneMgmtNote: "Les livres publiés apparaissent comme des œuvres terminées. Les storylines actives gardent la gestion des scènes pour les propriétaires.", noPublicStorylines: "Aucune Storyline publique à afficher pour le moment.", canon: "canon", lastUpdated: "Dernière mise à jour", created: "Créé", open: "Ouvrir", sceneManagement: "Gestion des scènes", sceneManagementTitle: "Réservé au propriétaire. Ouvre Writer Studio pour cette Storyline.", publishedOnAmazon: "Publié sur Amazon", publishedBook: "Livre publié", viewPublishedBook: "Voir le livre publié", viewOnAmazon: "Voir sur Amazon", by: "Par", activeStoryline: "Storyline active", ownerWorkshop: "Espace propriétaire",
    updateOk: "Profil mis à jour.", updateFailed: "Échec de la mise à jour du profil.",
    welcomeEnabled: "Message d’accueil activé (affiché à chaque connexion).", welcomeDisabled: "Message d’accueil désactivé.",
    uploadAuthorPhoto: "Téléverser la photo d’auteur", photoUploadHint: "JPG, PNG ou WEBP. Maximum 5 Mo.", photoUploading: "Téléversement…", photoUploadSuccess: "Photo téléversée. Enregistrez les modifications pour la publier sur votre profil.", photoUploadFailed: "Échec du téléversement de la photo.", removePhoto: "Retirer la photo", photoHelp: "Aide photo", photoHelpText: "Utilisez une photo d’auteur claire ou une image professionnelle. Restez respectueux et facile à reconnaître.", facebookSignIn: "Connexion Facebook", facebookLinked: "Facebook lié", facebookNotLinked: "Facebook non lié", linkFacebook: "Lier Facebook", linkingFacebook: "Liaison…", facebookLinkOk: "La connexion Facebook est liée à ce compte NextScenes.", facebookLinkHelp: "Liez Facebook une fois, puis utilisez-le pour les prochaines connexions.", facebookHelpSummary: "Aide connexion Facebook", facebookHelpNewUsers: "Les nouveaux utilisateurs peuvent s’inscrire directement avec Facebook si Facebook fournit un email.", facebookHelpExisting: "Les utilisateurs existants doivent d’abord se connecter avec leur méthode habituelle, puis lier Facebook ici. L’email Facebook doit correspondre à l’email du compte NextScenes.", facebookHelpAfterLink: "Après la liaison, Continuer avec Facebook fonctionnera pour les prochaines connexions.", facebookHelpWrongAccount: "Si Facebook affiche la mauvaise personne, déconnectez-vous de Facebook dans ce navigateur ou utilisez une fenêtre privée.", facebookLinkFailed: "Échec de la liaison Facebook.", facebookNotConfigured: "La connexion Facebook n’est pas encore configurée.",
  },
};

function currentProfileLang() {
  try {
    return (window.localStorage.getItem("ns.lang") || "en").toLowerCase().startsWith("fr") ? "fr" : "en";
  } catch {
    return "en";
  }
}

export default function Profile({ onNavigatePage }) {
  const { isAuthenticated, booting, refreshMe, user: currentUser } = useUser();
  const lang = currentProfileLang();
  const txt = PROFILE_TEXT[lang] || PROFILE_TEXT.en;

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [facebookReady, setFacebookReady] = useState(false);
  const [facebookLinking, setFacebookLinking] = useState(false);
  const [facebookLoadProblem, setFacebookLoadProblem] = useState("");
  const [facebookReloadKey, setFacebookReloadKey] = useState(0);

  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [storylines, setStorylines] = useState([]);

  const [editing, setEditing] = useState(false);

  // ✅ Owner preference: show Welcome greeting on login (per-account)
  const [showWelcomeGreeting, setShowWelcomeGreeting] = useState(true);

  // form fields (owner edit only)
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");

  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState("");
  const [location, setLocation] = useState("");
  const [bio, setBio] = useState("");

  const [website, setWebsite] = useState("");
  const [xUrl, setXUrl] = useState("");
  const [instagramUrl, setInstagramUrl] = useState("");

  function retryFacebookSignInLoad() {
    setFacebookReady(false);
    setFacebookLoadProblem("");

    try {
      const existing = document.getElementById("facebook-jssdk");
      if (existing && !window.FB) existing.remove();
    } catch {}

    setFacebookReloadKey((n) => n + 1);
  }

  useEffect(() => {
    if (!FACEBOOK_APP_ID) {
      setFacebookReady(false);
      setFacebookLoadProblem("not_configured");
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    let attempts = 0;
    const maxAttempts = 48; // about 12 seconds
    const scriptId = "facebook-jssdk";

    setFacebookReady(false);
    setFacebookLoadProblem("");

    function stopTimer() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    function markProblem(kind) {
      if (cancelled) return;
      setFacebookReady(false);
      setFacebookLoadProblem(kind);
    }

    function initFacebook() {
      if (cancelled) return true;
      if (!window.FB) return false;

      try {
        window.FB.init({
          appId: FACEBOOK_APP_ID,
          cookie: false,
          xfbml: false,
          version: "v25.0",
        });
        setFacebookReady(true);
        setFacebookLoadProblem("");
        return true;
      } catch (err) {
        console.warn("Facebook profile-link SDK failed to initialize:", err);
        markProblem("failed");
        return true;
      }
    }

    function waitForFacebook() {
      if (initFacebook()) return;

      stopTimer();
      timer = window.setInterval(() => {
        attempts += 1;

        if (initFacebook()) {
          stopTimer();
          return;
        }

        if (attempts >= maxAttempts) {
          stopTimer();
          markProblem("slow");
        }
      }, 250);
    }

    if (!document.getElementById("facebook-root")) {
      const root = document.createElement("div");
      root.id = "facebook-root";
      document.body.appendChild(root);
    }

    const existing = document.getElementById(scriptId);
    if (!existing) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.async = true;
      script.defer = true;
      script.crossOrigin = "anonymous";
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.onload = () => waitForFacebook();
      script.onerror = () => markProblem("failed");
      document.body.appendChild(script);
    } else {
      waitForFacebook();
    }

    return () => {
      cancelled = true;
      stopTimer();
    };
  }, [facebookReloadKey]);

  /* ------------------ helpers ------------------ */

  function safeTrim(v) {
    return String(v || "").trim();
  }

  function safeArray(v) {
    return Array.isArray(v) ? v : [];
  }

  function clampInt(n, min, max, fallback) {
    const x = Number(n);
    if (!Number.isFinite(x)) return fallback;
    const v = Math.floor(x);
    if (v < min) return min;
    if (v > max) return max;
    return v;
  }

  function inferCanonCount(story) {
    const candidates = [
      story?.canonCount,
      story?.canonScenes,
      story?.canonSceneCount,
      story?.canonApprovedCount,
      story?.canonApproved,
      story?.canonTotal,
      story?.canon,
      story?.canonWritten,
    ];

    for (const c of candidates) {
      const n = Number(c);
      if (Number.isFinite(n)) return Math.max(0, Math.floor(n));
    }

    const scenes = safeArray(story?.scenes);
    if (scenes.length) {
      const approved = scenes.filter((s) => s && (s.isCanon === true || s.status === "canon" || s.approved === true));
      return approved.length;
    }

    return 0;
  }

  function fmtDateTime(raw) {
    try {
      const d = raw ? new Date(raw) : null;
      if (!d || Number.isNaN(d.getTime())) return "—";
      return d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "—";
    }
  }

  // ✅ v11+ (kept): compute ALL plausible idents App.js might use (id/email/username)
  function welcomeIdentsFromUser(u) {
    try {
      const out = [];

      const id = safeTrim(u?.id || u?._id);
      if (id) out.push(id);

      const email = safeTrim(u?.email).toLowerCase();
      if (email) out.push(email);

      const un = safeTrim(u?.username).toLowerCase();
      if (un) out.push(un);

      const uniq = [];
      const seen = new Set();
      for (const v of out) {
        const k = String(v || "");
        if (!k) continue;
        if (seen.has(k)) continue;
        seen.add(k);
        uniq.push(k);
      }
      return uniq.length ? uniq : ["unknown"];
    } catch {
      return ["unknown"];
    }
  }

  function welcomeDisableKeysForUser(u) {
    const idents = welcomeIdentsFromUser(u);
    return idents.map((ident) => `${WELCOME_NS}.disabled.${encodeURIComponent(ident)}`);
  }

  // ✅ FINAL RULE:
  // Show at EVERY login until disabled.
  // Therefore: DO NOT use legacy "seen" as a blocker.
  function readShowWelcomePref(u) {
    try {
      const disableKeys = welcomeDisableKeysForUser(u);
      const anyDisabled = disableKeys.some((k) => window.localStorage.getItem(k) === "1");
      return !anyDisabled;
    } catch {
      return true;
    }
  }

  // ✅ Keep legacy key for backward compatibility only:
  // - When disabling: write legacy seen (old builds might look at it)
  // - When enabling: clear legacy seen (so old builds won't suppress)
  function setShowWelcomePref(u, nextShow) {
    try {
      const disableKeys = welcomeDisableKeysForUser(u);

      if (nextShow) {
        for (const k of disableKeys) window.localStorage.removeItem(k);
        window.localStorage.removeItem(WELCOME_LEGACY_SEEN_KEY);
      } else {
        for (const k of disableKeys) window.localStorage.setItem(k, "1");
        window.localStorage.setItem(WELCOME_LEGACY_SEEN_KEY, "1");
      }
    } catch {}
  }

  // ✅ Preview:
  // - Ensure enabled
  // - Set App.js preview flag (sessionStorage) so it opens immediately
  // - Navigate Home
  function previewWelcomeGreeting(u) {
    try {
      setShowWelcomeGreeting(true);
      setShowWelcomePref(u, true);
      window.sessionStorage.setItem(WELCOME_PREVIEW_KEY, "1");
    } catch {}

    setOk("Preview armed. Opening Home now.");
    setErr("");

    if (onNavigatePage) {
      onNavigatePage("home");
      return;
    }
    window.location.href = "/";
  }

  const isOwnerView = true;

  const roleLabel = useMemo(() => String(profile?.role || "user").toLowerCase(), [profile]);

  const displayName = useMemo(() => {
    const n = safeTrim(profile?.name || name);
    const u = safeTrim(profile?.username || username);
    return n || u || "Unknown";
  }, [profile, name, username]);

  const handle = useMemo(() => {
    const u = safeTrim(profile?.username || username);
    return u ? `@${u}` : "@unknown";
  }, [profile, username]);

  const initials = useMemo(() => {
    const u = safeTrim(profile?.username || username);
    const n = safeTrim(profile?.name || name);
    const base = (n || u || "U").trim();
    const parts = base.split(" ").filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return base.slice(0, 1).toUpperCase();
  }, [profile, name, username]);

  const memberSince = useMemo(() => {
    const raw = profile?.createdAt || profile?.created_at || "";
    if (!raw) return "";
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" });
  }, [profile]);

  function normalizeUrl(raw) {
    const v = safeTrim(raw);
    if (!v) return "";
    if (/^https?:\/\//i.test(v)) return v;
    return `https://${v}`;
  }

  const websiteUrl = useMemo(() => normalizeUrl(profile?.website || website), [profile, website]);
  const xLink = useMemo(() => normalizeUrl(profile?.xUrl || xUrl), [profile, xUrl]);
  const instagramLink = useMemo(() => normalizeUrl(profile?.instagramUrl || instagramUrl), [profile, instagramUrl]);

  const hasLinks = Boolean(websiteUrl || xLink || instagramLink);

  // ✅ IMPORTANT FIX (hooks order): keep memo before any early return
  const resolvedAvatar = useMemo(() => {
    const stateAvatar = safeTrim(avatarUrl);
    const savedAvatar = safeTrim(profile?.avatarUrl);
    return stateAvatar || savedAvatar;
  }, [avatarUrl, profile]);

  function pillStyle({ tone = "soft", strong = false } = {}) {
    const base = {
      padding: "7px 12px",
      borderRadius: 999,
      fontWeight: strong ? 950 : 850,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      whiteSpace: "nowrap",
      lineHeight: 1,
    };

    if (tone === "ink") {
      return { ...base, background: "#0f172a", color: "#ffffff", border: "1px solid #0f172a" };
    }
    if (tone === "paper") {
      return { ...base, background: "#f6f2ea", color: "#0f172a", border: "1px solid #eadfce" };
    }
    return { ...base, background: "#ffffff", color: "#0f172a", border: "1px solid #e5e7eb" };
  }

  function subtleCardStyle() {
    return { border: "1px solid #e5e7eb", borderRadius: 20, padding: 16, background: "#fff" };
  }

  function normalizePublicShelfTitle(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/[’']/g, "")
      .replace(/[^a-z0-9àâçéèêëîïôûùüÿñæœ\s-]/gi, " ")
      .replace(/\s+/g, " ")
      .trim();
  }

  function isPublicShelfStatusPublished(story) {
    const raw = String(story?.publicationStatus || story?.publishingStatus || "").trim().toLowerCase();
    return (
      raw === "publishedamazon" ||
      raw === "published_amazon" ||
      raw === "published" ||
      raw === "amazon" ||
      raw === "completed" ||
      raw === "complete" ||
      raw === "publishedexternal" ||
      raw === "published_external" ||
      raw === "external"
    );
  }

  function isKnownPublicShelfPublishedBook(story) {
    const title = normalizePublicShelfTitle(story?.title || story?.publishedTitle || story?.publicationTitle);

    return (
      title.includes("reflections of the wayfarer") ||
      title.includes("reflections of wayfarer") ||
      title.includes("réflexions du voyageur") ||
      title.includes("reflexions du voyageur") ||
      title.includes("réflexions dun voyageur") ||
      title.includes("reflexions dun voyageur") ||
      title.includes("réflexions d un voyageur") ||
      title.includes("reflexions d un voyageur") ||
      title.includes("ugo and her pet dog") ||
      title.includes("ugo et son petit chien") ||
      title.includes("ugo and the butterfly woman") ||
      title.includes("ugo et la femme papillon") ||
      title.includes("ugo et la femme-papillon")
    );
  }

  function getPublicShelfPublicationInfo(story, lang = "en") {
    const known = isKnownPublicShelfPublishedBook(story);
    const alreadyPublished = isPublicShelfStatusPublished(story);
    const storyForPublication = known && !alreadyPublished
      ? { ...(story || {}), publicationStatus: "publishedAmazon" }
      : story;

    return getPublicationInfo(storyForPublication, lang);
  }

  async function copyText(text) {
    const t = safeTrim(text);
    if (!t) return false;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(t);
        return true;
      }
    } catch {}

    try {
      const ta = document.createElement("textarea");
      ta.value = t;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      return true;
    } catch {
      return false;
    }
  }

  function publicProfileLink() {
    const u = safeTrim(profile?.username || username);
    if (!u) return "";
    const origin = window?.location?.origin || "";
    return `${origin}/u/${u}`;
  }

  function openStory(st) {
    const id = st?.id || st?._id;
    if (!id) return;

    if (onNavigatePage) {
      onNavigatePage("readerView", { storyId: id });
      return;
    }
    window.location.href = `/reader-view?storyId=${encodeURIComponent(id)}`;
  }

  function openWriterStudioForStory(st) {
    const id = st?.id || st?._id;
    if (!id) return;

    if (onNavigatePage) {
      onNavigatePage("writerStudio", { storyId: id });
      return;
    }
    window.location.href = `/writer-studio?storyId=${encodeURIComponent(id)}`;
  }

  const statTile = (label, value) => (
    <div className="ns-stat-tile">
      <div className="ns-stat-label">{label}</div>
      <div className="ns-stat-value">{Number(value || 0)}</div>
    </div>
  );

  function inputStyle({ mono } = {}) {
    return {
      padding: 10,
      borderRadius: 12,
      border: "1px solid #e5e7eb",
      width: "100%",
      outline: "none",
      background: busy ? "#f8fafc" : "#fff",
      fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" : "inherit",
    };
  }

  function isFacebookLinked() {
    return Boolean(
      profile?.facebookLinked ||
        currentUser?.facebookLinked ||
        profile?.facebookId ||
        currentUser?.facebookId ||
        String(profile?.authProvider || currentUser?.authProvider || "").toLowerCase() === "facebook"
    );
  }

  function normalizeFacebookLinkError(message) {
    const raw = String(message || "");
    const m = raw.toLowerCase();

    if (m.includes("different email")) {
      return txt.facebookHelpExisting;
    }

    if (m.includes("already linked")) {
      return raw || txt.facebookLinkFailed;
    }

    if (m.includes("missing") || m.includes("invalid")) {
      return txt.facebookHelpWrongAccount;
    }

    return raw || txt.facebookLinkFailed;
  }

  async function handleLinkFacebook() {
    if (!FACEBOOK_APP_ID || !window.FB) {
      setErr(txt.facebookNotConfigured);
      setOk("");
      return;
    }

    setErr("");
    setOk("");
    setFacebookLinking(true);

    window.FB.login(
      (facebookResponse) => {
        (async () => {
          try {
            const authResponse = facebookResponse?.authResponse || {};
            const accessToken = String(authResponse.accessToken || "").trim();
            const userID = String(authResponse.userID || "").trim();

            if (!accessToken) {
              throw new Error(txt.facebookLinkFailed);
            }

            const data = await api.post("/api/auth/facebook/link", { accessToken, userID }, { withCredentials: true });

            try {
              if (typeof refreshMe === "function") await refreshMe();
            } catch {}

            setProfile((prev) => ({
              ...(prev || {}),
              ...(data?.user || {}),
              facebookLinked: true,
              authProvider: data?.user?.authProvider || prev?.authProvider || "local",
              avatarUrl: data?.user?.avatarUrl || prev?.avatarUrl || "",
            }));

            setOk(data?.message || txt.facebookLinkOk);
          } catch (e) {
            const msg = e?.data?.message || e?.response?.data?.message || e?.message || txt.facebookLinkFailed;
            setErr(normalizeFacebookLinkError(msg));
          } finally {
            setFacebookLinking(false);
          }
        })();
      },
      { scope: "public_profile,email", return_scopes: true }
    );
  }

  /* ------------------ data loading ------------------ */

  async function loadOwnerProfile() {
    if (!isAuthenticated) return;

    setBusy(true);
    setErr("");
    setOk("");

    try {
      const data = await api("/api/profile", { method: "GET" });
      const u = data?.user || null;

      setProfile(u);
      setStats(data?.stats || null);
      setStorylines(Array.isArray(data?.storylines) ? data.storylines : []);

      setName(String(u?.name || ""));
      setUsername(String(u?.username || ""));

      setAvatarUrl(String(u?.avatarUrl || ""));
      setAvatarMessage("");
      setLocation(String(u?.location || ""));
      setBio(String(u?.bio || ""));

      setWebsite(String(u?.website || ""));
      setXUrl(String(u?.xUrl || ""));
      setInstagramUrl(String(u?.instagramUrl || ""));

      // ✅ init Welcome pref (after we have the user object)
      setShowWelcomeGreeting(readShowWelcomePref(u));

      setLoaded(true);
    } catch (e) {
      setErr(e?.message || "Failed to load profile.");
      setProfile(null);
      setStats(null);
      setStorylines([]);
      setLoaded(true);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (booting) return;

    if (!isAuthenticated) {
      if (onNavigatePage) onNavigatePage("login");
      return;
    }

    loadOwnerProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booting, isAuthenticated]);

  async function uploadAvatarFile(file) {
    if (!file || !isAuthenticated) return;

    setAvatarUploading(true);
    setAvatarMessage("");
    setErr("");
    setOk("");

    try {
      const uploaded = await api.uploadAuthorPhoto(file);
      const url = String(uploaded?.publicUrl || uploaded?.url || "").trim();
      if (!url) throw new Error(txt.photoUploadFailed);
      setAvatarUrl(url);
      setAvatarMessage(txt.photoUploadSuccess);
    } catch (e) {
      setAvatarMessage("");
      setErr(e?.message || txt.photoUploadFailed);
    } finally {
      setAvatarUploading(false);
    }
  }

  async function save() {
    if (!isAuthenticated) return;

    setBusy(true);
    setErr("");
    setOk("");

    try {
      const payload = { name, username, avatarUrl, location, bio, website, xUrl, instagramUrl };

      const data = await api("/api/profile", {
        method: "PATCH",
        body: JSON.stringify(payload),
      });

      const u = data?.user || null;

      setProfile(u);
      setStats(data?.stats || null);
      setStorylines(Array.isArray(data?.storylines) ? data.storylines : storylines);

      setName(String(u?.name || ""));
      setUsername(String(u?.username || ""));

      setAvatarUrl(String(u?.avatarUrl || ""));
      setAvatarMessage("");
      setLocation(String(u?.location || ""));
      setBio(String(u?.bio || ""));

      setWebsite(String(u?.website || ""));
      setXUrl(String(u?.xUrl || ""));
      setInstagramUrl(String(u?.instagramUrl || ""));

      setOk(txt.updateOk);
      setEditing(false);
    } catch (e) {
      setErr(e?.message || txt.updateFailed);
    } finally {
      setBusy(false);
    }
  }

  /* ------------------ render ------------------ */

  if (booting) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 1120, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0, marginBottom: 6 }}>{txt.title}</h2>
          <p style={{ marginTop: 0, color: "#475569" }}>{txt.restoring}</p>
        </div>
      </div>
    );
  }

  const headerTitle = txt.title;
  const headerSubtitle = txt.subtitle;

  return (
    <div className="page ns-profile-page">
      <div className="ns-profile-shell" style={{ maxWidth: 1120, margin: "0 auto" }}>
        {/* Shared NS Hero */}
        <header
          className="ns-identity-hero ns-profile-hero"
          style={{
            backgroundImage: `linear-gradient(90deg, rgba(4, 31, 24, 0.94) 0%, rgba(4, 47, 34, 0.84) 48%, rgba(4, 31, 24, 0.68) 100%), url(${process.env.PUBLIC_URL}/images/storyhub/storyhub-background.png)`,
          }}
        >
          <div className="ns-identity-hero-copy">
            <p
              className="ns-identity-kicker"
              style={{
                color: "#D9A441",
                borderColor: "rgba(217, 164, 65, 0.52)",
                background: "rgba(217, 164, 65, 0.10)",
                textShadow: "0 1px 10px rgba(217, 164, 65, 0.18)",
              }}
            >
              NEXTSCENES
            </p>
            <h1 className="ns-identity-title">{headerTitle}</h1>
            <p className="ns-identity-subtitle">{headerSubtitle}</p>

            <div className="ns-identity-actions">
              <span className="ns-card-meta ns-identity-status">{txt.ownerView}</span>
              {memberSince ? <span className="ns-card-meta ns-identity-status">{txt.memberSince} {memberSince}</span> : null}

              <button className="ns-button ns-button-outline" type="button" onClick={loadOwnerProfile} disabled={busy}>
                {busy ? txt.working : txt.refresh}
              </button>

              {safeTrim(profile?.username || username) ? (
                <>
                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={async () => {
                      const link = publicProfileLink();
                      if (!link) return;
                      const okCopy = await copyText(link);
                      setOk(okCopy ? txt.copied : txt.copyFailed);
                      setErr("");
                    }}
                    title={txt.copyTitle}
                  >{txt.copyLink}</button>

                  <button
                    className="ns-button ns-button-outline"
                    type="button"
                    onClick={() => {
                      const link = publicProfileLink();
                      if (!link) return;
                      window.open(link, "_blank", "noopener,noreferrer");
                    }}
                    title={txt.previewTitle}
                  >{txt.previewPublic}</button>
                </>
              ) : null}

              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={() => (onNavigatePage ? onNavigatePage("home") : (window.location.href = "/"))}
              >{txt.goHome}</button>
            </div>
          </div>

          <div className="ns-identity-hero-stat" aria-label={txt.profileSummary}>
            <span className="ns-identity-stat-number">
              {clampInt(stats?.storylinesInitiated || storylines.length, 0, 999999, 0)}
            </span>
            <span className="ns-identity-stat-label">
              {clampInt(stats?.storylinesInitiated || storylines.length, 0, 999999, 0) === 1 ? txt.storylineInitiated : txt.storylinesInitiated}
            </span>
          </div>
        </header>

        {err ? (
          <div className="banner" style={{ marginTop: 12 }}>
            {err}
          </div>
        ) : null}

        {ok ? (
          <div className="banner" style={{ marginTop: 12 }}>
            {ok}
          </div>
        ) : null}

        {!loaded ? (
          <div style={{ marginTop: 14, color: "#475569" }}>{txt.loading}</div>
        ) : !profile ? (
          <div style={{ marginTop: 14, color: "#475569" }}>{txt.noProfile}</div>
        ) : (
          <>
            {/* Author Hero */}
            <div className="ns-author-hero" style={{ marginTop: 16 }}>
              <div className="ns-author-hero-inner">
                <div className="ns-author-avatar" title="Avatar">
                  {resolvedAvatar ? (
                    <img
                      src={resolvedAvatar}
                      alt="avatar"
                      className="ns-author-avatar-img"
                      onError={() => {
                        if (!isOwnerView) return;
                        setAvatarUrl("");
                        setProfile((p) => (p ? { ...p, avatarUrl: "" } : p));
                      }}
                    />
                  ) : (
                    <span className="ns-author-avatar-initials">{initials}</span>
                  )}
                </div>

                <div className="ns-author-meta">
                  <div className="ns-author-name-row">
                    <div className="ns-author-name">{displayName}</div>
                    <span className="ns-author-role">{roleLabel}</span>
                  </div>

                  <div className="ns-author-handle">{handle}</div>

                  <div className="ns-author-badges">
                    <span style={pillStyle({ tone: "paper", strong: true })}>{txt.nsAuthor}</span>
                    {safeTrim(profile?.location || location) ? (
                      <span style={pillStyle({ tone: "soft" })}>{safeTrim(profile?.location || location)}</span>
                    ) : null}
                    <span style={pillStyle({ tone: "soft" })}>{txt.ownerView}</span>
                  </div>

                  <div className="ns-author-bio">
                    {safeTrim(profile?.bio || bio) ? (
                      <span style={{ whiteSpace: "pre-wrap" }}>{safeTrim(profile?.bio || bio)}</span>
                    ) : (
                      <span style={{ color: "#64748b", fontWeight: 500 }}>{txt.noBio}</span>
                    )}
                  </div>

                  {hasLinks ? (
                    <div className="ns-author-links">
                      {websiteUrl ? (
                        <a className="ns-link-chip" href={websiteUrl} target="_blank" rel="noreferrer" title="Website">
                          Website
                        </a>
                      ) : null}
                      {xLink ? (
                        <a className="ns-link-chip" href={xLink} target="_blank" rel="noreferrer" title="X">
                          X
                        </a>
                      ) : null}
                      {instagramLink ? (
                        <a className="ns-link-chip" href={instagramLink} target="_blank" rel="noreferrer" title="Instagram">
                          Instagram
                        </a>
                      ) : null}
                    </div>
                  ) : (
                    <div className="ns-author-links" style={{ color: "#64748b", fontWeight: 500 }}>
                      {txt.noLinks}
                    </div>
                  )}
                </div>

                <div className="ns-author-stats">
                  {statTile(txt.storylinesInitiatedTile, stats?.storylinesInitiated)}
                  {statTile(txt.canonScenesWritten, stats?.canonScenesWritten)}
                </div>
              </div>
            </div>

            {/* Owner Edit + Account cards */}
            <div
              className="ns-owner-grid"
              style={{ marginTop: 14, display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: 14, alignItems: "start" }}
            >
              <div className="ns-profile-details-card" style={subtleCardStyle()}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <h3 style={{ marginTop: 0, marginBottom: 6 }}>{txt.profileDetails}</h3>
                    <p style={{ marginTop: 0, color: "#475569" }}>
                      {txt.profileDetailsHelp}
                    </p>
                  </div>

                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button
                      className="btn"
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setErr("");
                        setOk("");
                        setEditing((v) => !v);
                      }}
                    >
                      {editing ? txt.closeEdit : txt.editProfile}
                    </button>

                    <button
                      className="btn"
                      type="button"
                      onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })}
                      title={txt.viewShelfTitle}
                    >{txt.viewShelf}</button>
                  </div>
                </div>

                {!editing ? (
                  <div className="ns-profile-detail-list" aria-label={txt.profileDetails}>
                    <div className="ns-profile-detail-row">
                      <span>{txt.name}</span>
                      <strong>{safeTrim(profile?.name) || "—"}</strong>
                    </div>
                    <div className="ns-profile-detail-row">
                      <span>{txt.username}</span>
                      <strong>{safeTrim(profile?.username) ? `@${safeTrim(profile.username)}` : "—"}</strong>
                    </div>
                    <div className="ns-profile-detail-row">
                      <span>{txt.location}</span>
                      <strong>{safeTrim(profile?.location) || "—"}</strong>
                    </div>

                    <div className="ns-profile-detail-mini-grid">
                      <div className="ns-profile-detail-mini-card">
                        <span>{txt.authorPhoto}</span>
                        <strong>{resolvedAvatar ? txt.photoAdded : txt.photoMissing}</strong>
                      </div>
                      <div className="ns-profile-detail-mini-card">
                        <span>{txt.publicLinks}</span>
                        <strong>{hasLinks ? txt.publicLinksReady : txt.publicLinksMissing}</strong>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 600 }}>{txt.displayName}</div>
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          style={inputStyle({})}
                          disabled={busy}
                          placeholder={txt.publicName}
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 600 }}>{txt.username}</div>
                        <input
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          style={inputStyle({ mono: true })}
                          disabled={busy}
                          placeholder="username"
                        />
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 600 }}>{txt.location}</div>
                      <input
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        style={inputStyle({})}
                        disabled={busy}
                        placeholder="City, Country"
                      />
                    </div>

                    <div className="ns-profile-photo-editor">
                      <div className="ns-profile-photo-preview" aria-label={txt.photoPreview}>
                        {safeTrim(avatarUrl) ? (
                          <img src={avatarUrl} alt="" />
                        ) : (
                          <span>{initials}</span>
                        )}
                      </div>

                      <div className="ns-profile-photo-editor-body">
                        <div className="ns-profile-photo-editor-title">{txt.authorPhoto}</div>
                        <p>{txt.photoUploadHint}</p>

                        <div className="ns-profile-photo-tools">
                          <label className="btn ns-btn-soft ns-profile-photo-upload">
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/webp"
                              disabled={busy || avatarUploading}
                              onChange={(e) => {
                                const file = e.target.files && e.target.files[0];
                                e.target.value = "";
                                if (file) uploadAvatarFile(file);
                              }}
                            />
                            {avatarUploading ? txt.photoUploading : txt.uploadAuthorPhoto}
                          </label>

                          {avatarUrl ? (
                            <button
                              className="btn ns-btn-soft"
                              type="button"
                              disabled={busy || avatarUploading}
                              onClick={() => {
                                setAvatarUrl("");
                                setAvatarMessage("");
                              }}
                            >
                              {txt.removePhoto}
                            </button>
                          ) : null}
                        </div>

                        {avatarMessage ? (
                          <div className="ns-profile-photo-note">{avatarMessage}</div>
                        ) : null}

                        <details className="ns-profile-photo-help">
                          <summary>{txt.photoHelp}</summary>
                          <div>{txt.photoHelpText}</div>
                        </details>

                        <details className="ns-profile-photo-help ns-profile-photo-advanced">
                          <summary>{txt.advancedPhotoUrl}</summary>
                          <input
                            value={avatarUrl}
                            onChange={(e) => {
                              setAvatarUrl(e.target.value);
                              setAvatarMessage("");
                            }}
                            style={inputStyle({ mono: true })}
                            disabled={busy || avatarUploading}
                            placeholder="https://..."
                          />
                        </details>
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 600 }}>{txt.bio}</div>
                      <textarea
                        value={bio}
                        onChange={(e) => setBio(e.target.value)}
                        style={{ ...inputStyle({}), minHeight: 170, resize: "vertical" }}
                        disabled={busy}
                        placeholder={txt.bioPlaceholder}
                      />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      <div>
                        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 600 }}>{txt.website}</div>
                        <input
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          style={inputStyle({ mono: true })}
                          disabled={busy}
                          placeholder="example.com"
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 600 }}>X</div>
                        <input
                          value={xUrl}
                          onChange={(e) => setXUrl(e.target.value)}
                          style={inputStyle({ mono: true })}
                          disabled={busy}
                          placeholder="x.com/..."
                        />
                      </div>

                      <div>
                        <div style={{ fontSize: 12, color: "#64748b", marginBottom: 6, fontWeight: 600 }}>Instagram</div>
                        <input
                          value={instagramUrl}
                          onChange={(e) => setInstagramUrl(e.target.value)}
                          style={inputStyle({ mono: true })}
                          disabled={busy}
                          placeholder="instagram.com/..."
                        />
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 4 }}>
                      <button className="btn" type="button" onClick={save} disabled={busy}>
                        {busy ? txt.saving : txt.saveChanges}
                      </button>

                      <button
                        className="btn"
                        type="button"
                        onClick={() => {
                          setEditing(false);
                          setErr("");
                          setOk("");
                          loadOwnerProfile();
                        }}
                        disabled={busy}
                      >{txt.cancel}</button>
                    </div>
                  </div>
                )}
              </div>

              <div className="ns-account-settings-card" style={subtleCardStyle()}>
                <h3 style={{ marginTop: 0, marginBottom: 6 }}>{txt.account}</h3>

                <div style={{ marginTop: 10 }}>
                  <div style={{ fontSize: 12, color: "#64748b", fontWeight: 600, marginBottom: 6 }}>{txt.emailPrivate}</div>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>{safeTrim(profile?.email) || "—"}</div>
                </div>

                <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div className="ns-stat-tile">
                    <div className="ns-stat-label">{txt.emailVerified}</div>
                    <div className="ns-stat-value">{profile?.emailVerified ? txt.yes : txt.no}</div>
                  </div>

                  <div className="ns-stat-tile">
                    <div className="ns-stat-label">{txt.status}</div>
                    <div className="ns-stat-value">{profile?.isActive ? txt.active : txt.disabled}</div>
                  </div>
                </div>

                <div
                  style={{ marginTop: 14, ...subtleCardStyle(), background: "#f8fafc" }}
                  role="group"
                  aria-label="Facebook sign-in link"
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
                    <div>
                      <h4 style={{ margin: "0 0 4px", color: "#0f172a" }}>{txt.facebookSignIn}</h4>
                      <div style={{ color: "#64748b", fontSize: 13, fontWeight: 500 }}>{txt.facebookLinkHelp}</div>
                      <details className="ns-facebook-help-details ns-facebook-help-details-profile">
                        <summary>{txt.facebookHelpSummary}</summary>
                        <div className="ns-facebook-help-body">
                          <p>{txt.facebookHelpNewUsers}</p>
                          <p>{txt.facebookHelpExisting}</p>
                          <p>{txt.facebookHelpAfterLink}</p>
                          <p>{txt.facebookHelpWrongAccount}</p>
                        </div>
                      </details>
                    </div>

                    <span style={pillStyle({ tone: isFacebookLinked() ? "ink" : "paper" })}>
                      {isFacebookLinked() ? txt.facebookLinked : txt.facebookNotLinked}
                    </span>
                  </div>

                  {!isFacebookLinked() ? (
                    <div style={{ marginTop: 12 }}>
                      <button
                        className="btn"
                        type="button"
                        disabled={busy || facebookLinking || !FACEBOOK_APP_ID || !facebookReady}
                        onClick={handleLinkFacebook}
                      >
                        {facebookLinking ? txt.linkingFacebook : txt.linkFacebook}
                      </button>
                    </div>
                  ) : null}

                  {!isFacebookLinked() && !facebookReady && facebookLoadProblem ? (
                    <div className="ns-social-provider-fallback" role="status" style={{ marginTop: 10 }}>
                      <span>{txt.facebookNotConfigured}</span>
                      {facebookLoadProblem !== "not_configured" ? (
                        <button type="button" className="ns-social-retry-button" onClick={retryFacebookSignInLoad}>
                          Retry Facebook
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                </div>

                {/* ✅ Preferences */}
                <div className="ns-pref-wrap">
                  <div className="ns-pref-head">
                    <div className="ns-pref-titleRow">
                      <div className="ns-pref-title">{txt.preferences}</div>
                      <span className="ns-pref-chip">{txt.prefOwner}</span>
                    </div>
                    <div className="ns-pref-sub">
                      {txt.prefSub}
                    </div>
                  </div>

                  <div className="ns-pref-card" role="group" aria-label="Welcome greeting preference">
                    <div className="ns-pref-cardTop">
                      <div className="ns-pref-cardTitle">{txt.welcomeGreeting}</div>
                      <div className="ns-pref-actions">
                        <button
                          className="btn ns-btn-soft"
                          type="button"
                          disabled={busy}
                          onClick={() => previewWelcomeGreeting(profile)}
                          title={txt.previewGreetingTitle}
                        >{txt.preview}</button>
                      </div>
                    </div>

                    <label
                      className="ns-pref-toggleRow"
                      title="Control whether the welcome greeting appears at every login"
                    >
                      <input
                        type="checkbox"
                        checked={showWelcomeGreeting}
                        disabled={busy}
                        onChange={(e) => {
                          const next = Boolean(e.target.checked);
                          setShowWelcomeGreeting(next);

                          setShowWelcomePref(profile, next);

                          setOk(next ? txt.welcomeEnabled : txt.welcomeDisabled);
                          setErr("");

                          // If turning OFF, make sure we are not leaving a preview flag armed
                          if (!next) {
                            try {
                              window.sessionStorage.removeItem(WELCOME_PREVIEW_KEY);
                            } catch {}
                          }
                        }}
                      />
                      <div className="ns-pref-toggleText">
                        <div className="ns-pref-toggleMain">{showWelcomeGreeting ? txt.enabled : txt.disabled}</div>
                        <div className="ns-pref-toggleHint">
                          {txt.prefEnabledHint}
                        </div>
                      </div>
                    </label>

                    <div className="ns-pref-footnote">
                      {txt.prefFootnote}
                    </div>
                  </div>
                  <NewsletterPreferencesV1 email={profile?.email || ""} language={lang} />
                </div>

                <div style={{ marginTop: 12, color: "#64748b", fontSize: 12, fontWeight: 500 }}>
                  {txt.storefront}
                </div>
              </div>
            </div>

            <MentorPanel onNavigatePage={onNavigatePage} />

            {/* Shelf */}
            <div className="ns-premium-shelf" style={{ marginTop: 14, ...subtleCardStyle(), padding: 18 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                  alignItems: "baseline",
                }}
              >
                <div>
                  <h3 style={{ marginTop: 0, marginBottom: 6 }}>{txt.publicShelf}</h3>
                  <p style={{ marginTop: 0, color: "#475569" }}>
                    {txt.shelfHelp}
                  </p>

                  <div style={{ marginTop: 6, fontSize: 12, color: "#64748b", fontWeight: 500 }}>
                    {txt.sceneMgmtNote}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={pillStyle({ tone: "paper" })}>{Array.isArray(storylines) ? storylines.length : 0} storylines</span>
                </div>
              </div>

              {!Array.isArray(storylines) || storylines.length === 0 ? (
                <div style={{ color: "#64748b", fontWeight: 500 }}>{txt.noPublicStorylines}</div>
              ) : (
                <div className="ns-shelf-grid-premium">
                  {storylines.map((st) => {
                    const key = st.id || st._id || `${st.title || "story"}-${Math.random().toString(36).slice(2)}`;
                    const forum = safeTrim(st?.forum);
                    const mode = safeTrim(st?.writingMode);

                    const publicationInfo = getPublicShelfPublicationInfo(st, lang);
                    const isPublishedBook = Boolean(publicationInfo?.isPublished);
                    const publishedTitle = safeTrim(publicationInfo?.publishedTitle || st?.publishedTitle || st?.title || "Published Book");
                    const publishedAuthor = safeTrim(publicationInfo?.publishedAuthorName || st?.publishedAuthorName || st?.publicationAuthorName || st?.authorName || "Goddy Oguzie");
                    const publishedCover = safeTrim(publicationInfo?.coverUrl);
                    const publishedGenre = forum || safeTrim(st?.category) || safeTrim(st?.genre);

                    const canonCount = inferCanonCount(st);
                    const lastUpdated = st?.updatedAt || st?.lastUpdated || st?.last_updated || st?.modifiedAt || "";
                    const created = st?.createdAt || st?.created_at || "";
                    const activeTitle = safeTrim(st?.title || st?.storyTitle) || "Untitled Storyline";
                    const activeSubtitle = safeTrim(st?.subtitle || st?.description || st?.synopsis || "");
                    const activeForum = forum || safeTrim(st?.category) || safeTrim(st?.genre) || "Unclassified Forum";

                    return (
                      <div key={key} className={`ns-shelf-card-premium${isPublishedBook ? " is-published-book" : ""}`}>
                        <button className="ns-shelf-plateBtn" type="button" onClick={() => openStory(st)} title={isPublishedBook ? txt.viewPublishedBook : txt.open}>
                          {isPublishedBook && publishedCover ? (
                            <img
                              src={publishedCover}
                              alt={publishedTitle}
                              className="ns-public-shelf-published-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="ns-public-shelf-active-cover" aria-hidden="true">
                              <StoryCoverCard story={st} />
                            </div>
                          )}
                        </button>

                        <div className="ns-shelf-meta-premium">
                          {isPublishedBook ? (
                            <div className="ns-public-shelf-published-info">
                              <div className="ns-public-shelf-published-title">{publishedTitle}</div>
                              {publishedAuthor ? (
                                <div className="ns-public-shelf-published-author">{txt.by} <strong>{publishedAuthor}</strong></div>
                              ) : null}
                              {publishedGenre ? <div className="ns-public-shelf-published-genre">{publishedGenre}</div> : null}
                              <div className="ns-public-shelf-published-badges">
                                <span className="ns-publication-chip is-published">{publicationInfo?.publicationLabel || txt.publishedOnAmazon}</span>
                              </div>
                            </div>
                          ) : (
                            <>
                              <div className="ns-public-shelf-active-info">
                                <div className="ns-public-shelf-active-kicker">{txt.activeStoryline}</div>
                                <div className="ns-public-shelf-active-title">{activeTitle}</div>
                                {activeSubtitle ? (
                                  <div className="ns-public-shelf-active-summary">{activeSubtitle}</div>
                                ) : null}
                              </div>

                              <div className="ns-shelf-toprow">
                                <div className="ns-shelf-tags-premium">
                                  {activeForum ? <span className="ns-tag">{activeForum}</span> : null}
                                  {mode ? <span className="ns-tag">{mode}</span> : null}
                                  <span className="ns-tag ns-tag-ink">{clampInt(canonCount, 0, 999999, 0)} {txt.canon}</span>
                                </div>
                              </div>

                              <div className="ns-shelf-dates">
                                <div className="ns-shelf-dateRow">
                                  <span className="ns-shelf-dateLabel">{txt.lastUpdated}</span>
                                  <span className="ns-shelf-dateValue">{fmtDateTime(lastUpdated)}</span>
                                </div>
                                <div className="ns-shelf-dateRow">
                                  <span className="ns-shelf-dateLabel">{txt.created}</span>
                                  <span className="ns-shelf-dateValue">{fmtDateTime(created)}</span>
                                </div>
                              </div>
                            </>
                          )}

                          <div className="ns-shelf-actions-premium">
                            <button className="btn" type="button" onClick={() => openStory(st)}>{isPublishedBook ? txt.viewPublishedBook : txt.open}</button>

                            {isPublishedBook && publicationInfo?.amazonHref ? (
                              <button
                                className="btn ns-btn-soft ns-amazon-action"
                                type="button"
                                onClick={() => window.open(publicationInfo.amazonHref, "_blank", "noopener,noreferrer")}
                              >{txt.viewOnAmazon}</button>
                            ) : null}

                            {!isPublishedBook ? (
                              <button
                                className="btn ns-btn-soft"
                                type="button"
                                onClick={() => openWriterStudioForStory(st)}
                                title={txt.sceneManagementTitle}
                              >{txt.sceneManagement}</button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <style>{`
              .ns-profile-shell {
                padding: 0;
                border: 0;
                background: transparent;
                box-shadow: none;
              }
              .ns-profile-page {
                background:
                  radial-gradient(circle at top left, rgba(15, 81, 50, 0.06), transparent 30%),
                  linear-gradient(180deg, #f8fafc 0%, #f6fbf8 100%);
              }
              .ns-profile-page .ns-identity-actions {
                align-items: center;
              }
              .ns-profile-page .ns-profile-details-card,
              .ns-profile-page .ns-account-settings-card {
                border-radius: 22px !important;
                border-color: rgba(15, 81, 50, 0.13) !important;
                background: linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(248,250,252,0.92) 100%) !important;
              }

              .ns-author-hero {
                border-radius: 22px;
                border: 1px solid rgba(15, 81, 50, 0.16);
                background:
                  radial-gradient(circle at 8% 16%, rgba(15, 81, 50, 0.09), transparent 34%),
                  linear-gradient(135deg, #ffffff 0%, #f6fbf8 58%, #ffffff 100%);
                overflow: hidden;
              }
              .ns-author-hero-inner {
                display: grid;
                grid-template-columns: 96px minmax(0, 1fr) 240px;
                gap: 16px;
                padding: 18px;
                align-items: start;
              }
              .ns-author-avatar {
                width: 86px;
                height: 86px;
                border-radius: 20px;
                border: 1px solid rgba(15, 23, 42, 0.12);
                background: rgba(255, 255, 255, 0.92);
                overflow: hidden;
                display: flex;
                align-items: center;
                justify-content: center;
              }
              .ns-author-avatar-img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
              }
              .ns-author-avatar-initials {
                font-weight: 950;
                font-size: 22px;
                color: #0f172a;
                letter-spacing: 0.5px;
              }
              .ns-author-meta {
                min-width: 240px;
              }
              .ns-author-name-row {
                display: flex;
                gap: 10px;
                align-items: center;
                flex-wrap: wrap;
              }
              .ns-author-name {
                font-weight: 950;
                font-size: 26px;
                color: #0f172a;
                line-height: 1.15;
              }
              .ns-author-role {
                padding: 6px 10px;
                border-radius: 999px;
                background: #0f5132;
                color: #ffffff;
                font-weight: 900;
                font-size: 12px;
                letter-spacing: 0.2px;
                text-transform: lowercase;
              }
              .ns-author-handle {
                margin-top: 4px;
                color: #475569;
                font-weight: 850;
              }
              .ns-author-badges {
                margin-top: 10px;
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
              }
              .ns-author-bio {
                margin-top: 12px;
                color: #0f172a;
                font-weight: 750;
                line-height: 1.55;
              }
              .ns-author-links {
                margin-top: 10px;
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                align-items: center;
              }
              .ns-link-chip {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                padding: 8px 12px;
                border-radius: 999px;
                border: 1px solid rgba(15, 23, 42, 0.16);
                background: rgba(255, 255, 255, 0.75);
                color: #0f172a;
                font-weight: 850;
                text-decoration: none;
              }
              .ns-link-chip:hover {
                text-decoration: underline;
              }
              .ns-author-stats {
                display: grid;
                grid-template-columns: 1fr;
                gap: 12px;
                align-content: start;
              }
              .ns-stat-tile {
                border: 1px solid rgba(15, 23, 42, 0.12);
                border-radius: 18px;
                padding: 16px;
                background: rgba(255, 255, 255, 0.78);
              }
              .ns-stat-label {
                font-size: 12px;
                color: #64748b;
                font-weight: 850;
              }
              .ns-stat-value {
                margin-top: 6px;
                font-weight: 950;
                font-size: 20px;
                color: #0f172a;
              }

              .ns-profile-detail-list {
                margin-top: 14px;
                display: grid;
                gap: 10px;
              }
              .ns-profile-detail-row {
                display: grid;
                grid-template-columns: 132px minmax(0, 1fr);
                gap: 12px;
                align-items: center;
                padding: 10px 12px;
                border: 1px solid rgba(15, 23, 42, 0.09);
                border-radius: 14px;
                background: rgba(248, 250, 252, 0.72);
              }
              .ns-profile-detail-row span,
              .ns-profile-detail-mini-card span {
                color: #64748b;
                font-size: 12px;
                font-weight: 900;
                letter-spacing: 0.02em;
              }
              .ns-profile-detail-row strong,
              .ns-profile-detail-mini-card strong {
                color: #0f172a;
                font-weight: 950;
                overflow-wrap: anywhere;
              }
              .ns-profile-detail-mini-grid {
                margin-top: 4px;
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 10px;
              }
              .ns-profile-detail-mini-card {
                display: grid;
                gap: 6px;
                padding: 12px;
                border-radius: 16px;
                border: 1px solid rgba(15, 81, 50, 0.12);
                background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96));
              }
              .ns-profile-photo-editor {
                display: grid;
                grid-template-columns: 118px minmax(0, 1fr);
                gap: 14px;
                align-items: start;
                padding: 14px;
                border-radius: 18px;
                border: 1px solid rgba(15, 81, 50, 0.14);
                background: linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.96));
              }
              .ns-profile-photo-preview {
                width: 104px;
                height: 104px;
                border-radius: 24px;
                border: 1px solid rgba(15, 23, 42, 0.12);
                background: #ffffff;
                overflow: hidden;
                display: flex;
                align-items: center;
                justify-content: center;
                color: #0f172a;
                font-weight: 950;
                font-size: 28px;
                box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
              }
              .ns-profile-photo-preview img {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
              }
              .ns-profile-photo-editor-body {
                min-width: 0;
              }
              .ns-profile-photo-editor-title {
                color: #0f172a;
                font-weight: 950;
                font-size: 15px;
              }
              .ns-profile-photo-editor-body p {
                margin: 5px 0 0;
                color: #64748b;
                font-size: 12px;
                font-weight: 800;
                line-height: 1.45;
              }
              .ns-profile-photo-advanced input {
                margin-top: 8px;
                width: 100%;
              }

              .ns-pref-wrap {
                margin-top: 14px;
                border-radius: 18px;
                border: 1px solid rgba(15, 23, 42, 0.14);
                background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.98) 100%);
                padding: 12px;
              }
              .ns-pref-head {
                padding: 6px 4px 10px 4px;
              }
              .ns-pref-titleRow {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 10px;
              }
              .ns-pref-title {
                font-weight: 950;
                font-size: 14px;
                color: #0f172a;
                letter-spacing: 0.2px;
                text-transform: uppercase;
              }
              .ns-pref-chip {
                padding: 6px 10px;
                border-radius: 999px;
                background: #0f5132;
                color: #ffffff;
                font-weight: 900;
                font-size: 12px;
              }
              .ns-pref-sub {
                margin-top: 6px;
                font-size: 12px;
                color: #475569;
                font-weight: 800;
                line-height: 1.45;
              }
              .ns-pref-card {
                margin-top: 10px;
                border-radius: 16px;
                border: 1px solid rgba(15, 23, 42, 0.14);
                background: rgba(255, 255, 255, 0.92);
                padding: 12px;
                box-shadow: 0 10px 22px rgba(2, 6, 23, 0.06);
              }
              .ns-pref-cardTop {
                display: flex;
                justify-content: space-between;
                align-items: center;
                gap: 10px;
                flex-wrap: wrap;
              }
              .ns-pref-cardTitle {
                font-weight: 950;
                color: #0f172a;
                font-size: 14px;
              }
              .ns-pref-actions {
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                align-items: center;
              }
              .ns-pref-toggleRow {
                margin-top: 10px;
                display: flex;
                gap: 10px;
                align-items: flex-start;
                padding: 10px 10px;
                border-radius: 14px;
                border: 1px solid rgba(15, 23, 42, 0.10);
                background: rgba(248,250,252,0.9);
                cursor: pointer;
                user-select: none;
              }
              .ns-pref-toggleRow input {
                margin-top: 3px;
                transform: scale(1.1);
              }
              .ns-pref-toggleText {
                flex: 1;
                min-width: 200px;
              }
              .ns-pref-toggleMain {
                font-weight: 950;
                color: #0f172a;
              }
              .ns-pref-toggleHint {
                margin-top: 4px;
                font-size: 12px;
                color: #475569;
                font-weight: 800;
                line-height: 1.45;
              }
              .ns-pref-footnote {
                margin-top: 10px;
                font-size: 12px;
                color: #64748b;
                font-weight: 800;
                line-height: 1.45;
              }

              .ns-premium-shelf {
                background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,0.86) 100%);
              }
              .ns-shelf-grid-premium {
                margin-top: 14px;
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 14px;
              }
              .ns-shelf-card-premium {
                border: 1px solid rgba(15, 23, 42, 0.10);
                border-radius: 22px;
                background: #ffffff;
                padding: 14px;
                display: grid;
                grid-template-columns: 156px minmax(0, 1fr);
                gap: 18px;
                align-items: stretch;
                box-shadow: 0 10px 24px rgba(2, 6, 23, 0.06);
              }
              .ns-shelf-plateBtn {
                width: 156px;
                padding: 0;
                border: 0;
                background: transparent;
                cursor: pointer;
                display: flex;
                align-items: flex-start;
              }
              .ns-shelf-meta-premium {
                min-width: 220px;
                display: flex;
                flex-direction: column;
                gap: 10px;
              }
              .ns-shelf-tags-premium {
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                align-items: center;
              }
              .ns-tag {
                padding: 6px 10px;
                border-radius: 999px;
                border: 1px solid #e5e7eb;
                font-weight: 850;
                color: #0f172a;
                background: #ffffff;
              }
              .ns-tag-ink {
                border-color: rgba(15, 23, 42, 0.14);
                background: rgba(15, 23, 42, 0.06);
              }
              .ns-shelf-dates {
                border: 1px solid rgba(15, 23, 42, 0.10);
                border-radius: 16px;
                background: rgba(248,250,252,0.75);
                padding: 10px 12px;
              }
              .ns-shelf-dateRow {
                display: flex;
                justify-content: space-between;
                gap: 12px;
                padding: 6px 0;
                border-bottom: 1px solid rgba(15, 23, 42, 0.07);
              }
              .ns-shelf-dateRow:last-child {
                border-bottom: none;
              }
              .ns-shelf-dateLabel {
                color: #64748b;
                font-weight: 850;
                font-size: 12px;
              }
              .ns-shelf-dateValue {
                color: #0f172a;
                font-weight: 900;
                font-size: 12px;
                text-align: right;
              }
              .ns-shelf-actions-premium {
                display: flex;
                gap: 10px;
                flex-wrap: wrap;
                margin-top: auto;
              }

              .ns-profile-page .btn.ns-btn-soft {
                background: rgba(15, 23, 42, 0.04);
                border: 1px solid rgba(15, 23, 42, 0.14);
                color: #0f172a;
                font-weight: 900;
              }
              .ns-profile-page .btn.ns-btn-soft:hover {
                background: rgba(15, 23, 42, 0.07);
              }

              .ns-profile-photo-tools {
                margin-top: 8px;
                display: flex;
                gap: 8px;
                flex-wrap: wrap;
                align-items: center;
              }
              .ns-profile-photo-upload {
                cursor: pointer;
              }
              .ns-profile-photo-upload input {
                display: none;
              }
              .ns-profile-photo-note {
                margin-top: 8px;
                color: #065f46;
                font-size: 12px;
                font-weight: 850;
                line-height: 1.4;
              }
              .ns-profile-photo-help {
                margin-top: 8px;
                border: 1px solid rgba(15, 23, 42, 0.10);
                border-radius: 12px;
                background: rgba(248,250,252,0.8);
                padding: 8px 10px;
                color: #475569;
                font-size: 12px;
                font-weight: 800;
                line-height: 1.45;
              }
              .ns-profile-photo-help summary {
                cursor: pointer;
                color: #0f172a;
                font-weight: 950;
              }
              .ns-profile-photo-help div {
                margin-top: 6px;
              }


              .ns-profile-details-card {
                position: relative;
                overflow: hidden;
              }
              .ns-profile-details-card:before {
                content: "";
                position: absolute;
                inset: 0 0 auto 0;
                height: 4px;
                background: linear-gradient(90deg, #0f5132, rgba(15,81,50,0.16), transparent);
              }
              .ns-account-settings-card {
                position: relative;
                overflow: hidden;
              }
              .ns-account-settings-card:before {
                content: "";
                position: absolute;
                inset: 0 0 auto 0;
                height: 4px;
                background: linear-gradient(90deg, #0f5132, rgba(15,81,50,0.16), transparent);
              }
              .ns-public-shelf-active-info {
                display: grid;
                gap: 5px;
                min-width: 0;
              }
              .ns-public-shelf-active-kicker {
                color: #0f5132;
                font-size: 11px;
                font-weight: 950;
                letter-spacing: 0.12em;
                text-transform: uppercase;
              }
              .ns-public-shelf-active-title {
                color: #0f172a;
                font-size: 18px;
                line-height: 1.18;
                font-weight: 950;
                overflow-wrap: anywhere;
              }
              .ns-public-shelf-active-summary {
                color: #475569;
                font-size: 12px;
                font-weight: 760;
                line-height: 1.45;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
              }
              .ns-shelf-card-premium:not(.is-published-book) {
                border-color: rgba(15, 81, 50, 0.13);
                background: linear-gradient(180deg, rgba(255,255,255,1), rgba(248,250,252,0.82));
              }
              .ns-shelf-card-premium.is-published-book {
                background:
                  radial-gradient(circle at top left, rgba(15,81,50,0.08), transparent 35%),
                  linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98)) !important;
              }

              @media (max-width: 980px) {
                .ns-author-hero-inner {
                  grid-template-columns: 86px 1fr;
                }
                .ns-author-stats {
                  grid-column: 1 / -1;
                  grid-template-columns: 1fr 1fr;
                }
                .ns-shelf-grid-premium {
                  grid-template-columns: 1fr;
                }
              }
              @media (max-width: 520px) {
                .ns-author-name {
                  font-size: 22px;
                }
                .ns-author-stats,
                .ns-profile-detail-mini-grid {
                  grid-template-columns: 1fr;
                }
                .ns-profile-detail-row,
                .ns-profile-photo-editor {
                  grid-template-columns: 1fr;
                }
                .ns-shelf-card-premium {
                  grid-template-columns: 1fr;
                }
                .ns-shelf-plateBtn {
                  width: 100%;
                }
              }
              @media (max-width: 1100px) {
                .ns-owner-grid {
                  grid-template-columns: 1fr !important;
                }
              }
            `}</style>
          </>
        )}
      </div>
    </div>
  );
}
